import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  InputAdornment,
  InputLabel,
} from "@mui/material";
import instance from "../../globals/axios";
import {
  handleMobileKeyDown,
  isNotNullOrEmptyArray,
} from "../../globals/utils";
import { useAlert } from "../../components/Alert";
import SoilSensorValuesCard from "../../components/soiltesting/SoilSensorValuesCard";
import CustomAppBar from "../../components/AppBar";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import {
  externalSoilTestReportValidationSchema,
  soilTestReportValidationSchema,
} from "../../components/Custom/ValidationSchema";
import useFormValidation from "../../components/Custom/FormValidation";

const FormTextField = ({ label, name, value, onChange, error, disabled }) => {
  return (
    <TextField
      label={label}
      name={name}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      required
      fullWidth
      disabled={disabled}
      error={Boolean(error)}
      helperText={error}
    />
  );
};

const SoilTestReportForm = () => {
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [locLoading, setLocLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [commodities, setCommodities] = useState([]);
  const sensorValueParams = searchParams.get("sensorValues");
  const sensorValues = sensorValueParams
    ? JSON.parse(decodeURIComponent(sensorValueParams))
    : null;

  const [existingFarm, setExistingFarm] = useState("yes");
  const [postOfficeOptions, setPostOfficeOptions] = useState([]);
  const [farms, setFarms] = useState([]);
  const [crops, setCrops] = useState([]);
  const [soilTypes, setSoilTypes] = useState([]);
  const [formData, setFormData] = useState({});
  const validationSchema =
    existingFarm === "yes"
      ? soilTestReportValidationSchema()
      : externalSoilTestReportValidationSchema();

  const { errors, validate } = useFormValidation(formData, validationSchema);

  const fetchCrops = async (farmId) => {
    if (!farmId) {
      return;
    }
    try {
      const response = await instance.get(
        `common/get-farm-crop-list/?farm_id=${farmId}`
      );
      const { crops } = response.data || {};
      if (isNotNullOrEmptyArray(crops)) {
        setCrops(crops);
      } else {
        showAlert("error", "No crops found for the selected farm.");
      }
    } catch (error) {
      console.error("Error fetching crops:", error);
    }
  };

  useEffect(() => {
    fetchCommodity();
    fetchFarms();
    fetchSoilTypes();
  }, []);

  const fetchSoilTypes = async () => {
    try {
      const response = await instance.get(
        "farmer/irrigation/?type=soil-texture"
      );
      const data = response.data;
      if (isNotNullOrEmptyArray(data)) {
        setSoilTypes(data);
      } else {
        showAlert("error", "No soil types found");
      }
    } catch (error) {
      console.error("Error fetching soil types:", error);
    }
  };

  // Fetch farms data
  const fetchFarms = async () => {
    try {
      const response = await instance.get("common/get-user-related-farm-list/");
      const { farms } = response.data || {};
      if (isNotNullOrEmptyArray(farms)) {
        setFarms(farms);
      } else {
        showAlert("error", "No farms found for the user.");
      }
    } catch (error) {
      console.error("Error fetching farms:", error);
    }
  };

  const handleFarmChange = (event, value) => {
    if (value) {
      fetchCrops(value.id);
      setFormData({
        ...formData,
        farm: value,
      });
    } else {
      setFormData({
        ...formData,
        farm: null,
      });
    }
  };

  const fetchCommodity = async () => {
    try {
      const res = await instance.get("/common/commodities/?type=CROP");
      setCommodities(res.data);
    } catch (error) {
      console.error(error);
      showAlert("Failed to fetch ");
    }
  };

  const handleCropChange = (event, value) => {
    setFormData({
      ...formData,
      crop: value,
    });
  };

  const handleExistingFarmChange = (event) => {
    const isExistingFarm = event.target.value;
    setExistingFarm(isExistingFarm);
    setFormData({});
  };

  const handleChange = (name, value) => {
    if (name == "pincode") {
      if (value.length === 6) {
        setLocLoading(true);
        instance
          .get(`common/pincode/${value}`)
          .then((response) => {
            const postOfficesData = response.data[0]?.PostOffice;
            if (postOfficesData && postOfficesData.length > 0) {
              const postOffices = postOfficesData.map(
                (postOffice) => postOffice.Name
              );
              setPostOfficeOptions(postOffices);
              setFormData((prevData) => ({
                ...prevData,
                state: postOfficesData[0].State,
                district: postOfficesData[0].District,
              }));
            } else {
              showAlert("error", response.data[0].Message);
              setFormData((prevData) => ({
                ...prevData,
                state: "",
                district: "",
              }));
              setPostOfficeOptions([]);
            }
          })
          .catch((error) => {
            console.error("Error Fetching Post Offices:", error);
            showAlert("error", "Failed to load location data");
          })
          .finally(() => {
            setLocLoading(false);
          });
      }
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let formattedFormData = {
        ...formData,
        farm: formData.farm?.id || null,
        crop: formData.crop?.id || null,
        ...sensorValues,
      };
      let errors = await validate(formattedFormData);
      if (errors && Object.keys(errors).length > 0) {
        showAlert("error", "Fix Validation Errors");
        return;
      }
      if (!formattedFormData.farm) {
        delete formattedFormData.farm;
      }
      if (!formattedFormData.crop) {
        delete formattedFormData.crop;
      }
      if (formData.sowing_date) {
        formattedFormData.sowing_date =
          formData.sowing_date.format("YYYY-MM-DD");
      }
      const response = await instance.post(
        "soil-testing/create-asqi-soil-test-report/",
        formattedFormData
      );
      const { status, report_id } = response.data || {};
      if (status == "SUCCESS" && report_id) {
        navigate(`/soil-test-report/${report_id}`, {
          replace: true,
        });
        showAlert("success", "Soil test report submitted successfully!");
      } else {
        showAlert("error", "Failed to submit soil test report");
      }
      // Reset form or redirect
    } catch (error) {
      console.error("Error submitting report:", error);
      showAlert("error", "Failed to submit soil test report");
    }
  };

  return (
    <>
      <Helmet>
        <title>Soil Test Report Form - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      {sensorValues ? (
        <Paper elevation={1} style={{ padding: "12px", margin: "12px" }}>
          <Typography variant="h5" gutterBottom>
            Soil Test Report Form
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SoilSensorValuesCard sensorValues={sensorValues} />
            </Grid>

            <Grid item xs={12}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle1">
                        Is this an existing farm?
                      </Typography>
                      <RadioGroup
                        row
                        name="existingFarm"
                        value={existingFarm}
                        onChange={handleExistingFarmChange}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {existingFarm === "yes" ? (
                    <>
                      <Grid item xs={12}>
                        <Autocomplete
                          options={farms}
                          getOptionLabel={(option) =>
                            option ? `${option.id}-${option.farm_name}` : ""
                          }
                          value={formData.farm || null}
                          onChange={handleFarmChange}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Farm"
                              required
                              error={Boolean(errors.farm)}
                              helperText={errors.farm}
                            />
                          )}
                        />
                      </Grid>
                      {formData.farm && (
                        <Grid item xs={12}>
                          <Autocomplete
                            options={crops}
                            getOptionLabel={(option) => option.crop_name}
                            onChange={handleCropChange}
                            value={formData.crop}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label={t("Select Crop")}
                                error={Boolean(errors.crop)}
                                helperText={errors.crop}
                              />
                            )}
                          />
                        </Grid>
                      )}
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormTextField
                          label="Farmer Name"
                          name="farmer_name"
                          value={formData.farmer_name}
                          onChange={handleChange}
                          error={errors.farmer_name}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Mobile"
                          name="mobile"
                          value={formData.mobile}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          required
                          variant="outlined"
                          error={Boolean(errors.mobile)}
                          helperText={errors.mobile}
                          inputProps={{
                            maxLength: 10,
                            inputMode: "numeric",
                          }}
                          onKeyDown={handleMobileKeyDown}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                +91
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Farm Area"
                          name="farm_area"
                          value={formData.farm_area}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          required
                          variant="outlined"
                          error={!!errors.farm_area}
                          helperText={errors.farm_area}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors.soil_type}>
                          <InputLabel>Soil Type *</InputLabel>
                          <Select
                            label="Soil Type"
                            value={formData.soil_type || ""}
                            onChange={(e) =>
                              handleChange("soil_type", e.target.value)
                            }
                          >
                            {soilTypes.map((soilType) => (
                              <MenuItem
                                key={soilType.name}
                                value={soilType.name}
                              >
                                {soilType.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={commodities}
                          getOptionLabel={(option) => option.name || ""}
                          value={formData.commodity || null}
                          onChange={(event, newValue) => {
                            setFormData({
                              ...formData,
                              commodity: newValue,
                              crop_name: newValue ? newValue.name : "",
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Crop"
                              required
                              variant="outlined"
                              error={!!errors.crop_name}
                              helperText={errors.crop_name}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label={t("Sowing Date")}
                            value={formData.sowing_date}
                            onChange={(newValue) => {
                              if (newValue) {
                                setFormData((prev) => ({
                                  ...prev,
                                  sowing_date: newValue,
                                }));
                              }
                            }}
                            slots={{ textField: TextField }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: Boolean(errors.sowing_date),
                                helperText: errors.sowing_date || "",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormTextField
                          key="pincode"
                          label={t("Pincode")}
                          name="pincode"
                          value={formData["pincode"] || ""}
                          onChange={handleChange}
                          type="text"
                          error={errors.pincode}
                        />
                      </Grid>
                      {locLoading ? (
                        <Grid item xs={12} md={6}>
                          <Typography>{`${t("Loading")}...`}</Typography>
                        </Grid>
                      ) : (
                        <>
                          <Grid item xs={12} md={6}>
                            <FormTextField
                              label={t("State")}
                              name="state"
                              value={formData.state || ""}
                              onChange={handleChange}
                              error={errors.state}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormTextField
                              label={t("District")}
                              name="district"
                              value={formData.district || ""}
                              onChange={handleChange}
                              error={errors.district}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Select
                              name="postoffice"
                              value={formData.postoffice || ""}
                              onChange={(e) =>
                                handleChange("postoffice", e.target.value)
                              }
                              fullWidth
                              placeholder="Post Office"
                              error={Boolean(errors.postoffice)}
                              helperText={
                                errors.postoffice ||
                                "Add Pincode to get post offices"
                              }
                              disabled={postOfficeOptions.length === 0}
                            >
                              {postOfficeOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} md={6}>
                        <FormTextField
                          label={t("Village")}
                          name="village"
                          value={formData.village}
                          onChange={handleChange}
                          error={errors.village}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormTextField
                          label={t("Address Line 1")}
                          name="address_line1"
                          value={formData.address_line1}
                          onChange={handleChange}
                          error={errors.address_line1}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      Submit Report
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <Typography variant="h4" gutterBottom>
          No sensor values available.
        </Typography>
      )}
    </>
  );
};

export default SoilTestReportForm;
